import React, { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '../components/ui'
import { ArrowLeft, Eye, EyeOff, Lock, Mail, ShieldCheck, User } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '../components/LanguageSwitcher'

export default function LoginPage() {
  const { login, verifyTwoFactor, isLoggingIn, isVerifyingTwoFactor } = useAuth()
  const { t, i18n } = useTranslation()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  // Two-factor login step (set when the account has 2FA enabled)
  const [challengeToken, setChallengeToken] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [recoveryMode, setRecoveryMode] = useState(false)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError('')
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      const response = await login(formData)
      if (response.twoFactorRequired) {
        // Valid credentials — but the account requires a second factor.
        // Nothing is stored yet; the challenge token is exchanged below.
        setChallengeToken(response.challengeToken)
        setCode('')
        setRecoveryMode(false)
      }
      // Navigation is handled by useAuth hook and App.tsx routing
    } catch (err: any) {
      setError(err.message || t('auth.validation.loginFailed'))
    }
  }

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      await verifyTwoFactor(
        recoveryMode
          ? { challengeToken, recoveryCode: code.trim() }
          : { challengeToken, code: code.trim() }
      )
      // Navigation is handled by useAuth hook and App.tsx routing
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || t('auth.twofa.step.invalidCode'))
    }
  }

  const backToCredentials = () => {
    setChallengeToken(null)
    setCode('')
    setRecoveryMode(false)
    setError('')
  }

  return (
    <div key={i18n.language} className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="container mx-auto">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/'}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md mx-auto">

        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                {challengeToken ? (
                  <ShieldCheck className="h-6 w-6 text-primary-600" />
                ) : (
                  <User className="h-6 w-6 text-primary-600" />
                )}
                <span>
                  {challengeToken ? t('auth.twofa.step.title') : t('auth.login.title')}
                </span>
              </div>
              <p className="text-sm font-normal text-neutral-600">
                {challengeToken
                  ? t('auth.twofa.step.description')
                  : t('auth.login.subtitle')}
              </p>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {challengeToken ? (
              <form onSubmit={handleVerify} className="space-y-4">
                <Input
                  label={recoveryMode
                    ? t('auth.twofa.step.recoveryCode')
                    : t('auth.twofa.step.code')}
                  type="text"
                  inputMode={recoveryMode ? 'text' : 'numeric'}
                  autoComplete="one-time-code"
                  placeholder={recoveryMode ? 'xxxxx-xxxxx' : '000000'}
                  value={code}
                  maxLength={recoveryMode ? undefined : 6}
                  onChange={(e) => {
                    setCode(e.target.value)
                    if (error) setError('')
                  }}
                  autoFocus
                  required
                  leftIcon={<ShieldCheck className="h-4 w-4 text-neutral-400" />}
                />

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="islamic"
                  className="w-full"
                  disabled={isVerifyingTwoFactor || code.length === 0}
                >
                  {isVerifyingTwoFactor
                    ? t('auth.twofa.step.verifying')
                    : t('auth.twofa.step.verify')}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-primary-600 hover:text-primary-700 font-medium"
                    onClick={() => {
                      setRecoveryMode(!recoveryMode)
                      setCode('')
                      setError('')
                    }}
                  >
                    {recoveryMode
                      ? t('auth.twofa.step.useAppCode')
                      : t('auth.twofa.step.useRecoveryCode')}
                  </button>
                  <button
                    type="button"
                    className="text-neutral-500 hover:text-neutral-700 font-medium"
                    onClick={backToCredentials}
                  >
                    {t('auth.twofa.step.back')}
                  </button>
                </div>
              </form>
            ) : (
              <>
                <form onSubmit={handleLogin} className="space-y-4">
                  <Input
                    label={t('auth.login.email')}
                    type="email"
                    placeholder={t('auth.login.emailPlaceholder')}
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                    leftIcon={<Mail className="h-4 w-4 text-neutral-400" />}
                  />

                  <div className="relative">
                    <Input
                      label={t('auth.login.password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={t('auth.login.passwordPlaceholder')}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      required
                      leftIcon={<Lock className="h-4 w-4 text-neutral-400" />}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-9 text-neutral-400 hover:text-neutral-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="islamic"
                    className="w-full"
                    disabled={isLoggingIn}
                  >
                    {isLoggingIn ? t('auth.login.signingIn') : t('auth.login.signIn')}
                  </Button>
                  <div className="text-center">
                    <a href="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 hover:underline">
                      Forgot your password?
                    </a>
                  </div>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-neutral-600">
                    {t('auth.login.noAccount')}{' '}
                    <button
                      onClick={() => window.location.href = '/register'}
                      className="text-primary-600 hover:text-primary-700 font-medium"
                    >
                      {t('auth.login.signUp')}
                    </button>
                  </p>
                </div>

                <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <h4 className="font-semibold text-primary-900 mb-2">{t('auth.login.needHelp.title')}</h4>
                  <p className="text-sm text-primary-700 mb-2">
                    {t('auth.login.needHelp.description')}
                  </p>
                  <p className="text-xs text-primary-600">
                    {t('auth.login.needHelp.email')}: support@akhirat.co
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  )
}
